import { ensureSyntaxTree, syntaxTree } from "@codemirror/language";
import type { Diagnostic } from "@codemirror/lint";
import type { EditorState, Text } from "@codemirror/state";
import type { Tree } from "@lezer/common";

// Syntax errors read off an error-tolerant Lezer tree (the Rhai grammar,
// lang-json): unlike a parser that stops at the first mistake, the tree
// marks every spot it could not place, so an editor can list several at
// once. A zero-width error node means something is missing at that point;
// otherwise the covered text is what the parser could not place.

/** How many to show: an error-tolerant parser can cascade after a real
 *  mistake, so the list stays short. */
export const MAX_SYNTAX_ERRORS = 3;

export interface SyntaxErrorOptions {
  /** Lines already covered by a better message (the engine's, or
   *  JSON.parse's) — skipped, one error per line otherwise. */
  skipLines?: ReadonlySet<number>;
  max?: number;
  /** Wording for a zero-width error (something missing at that point). */
  missingMessage?: string;
}

/** The error nodes of `tree` over `doc`, one per line, the first few. */
export function syntaxErrorsIn(
  tree: Tree,
  doc: Text,
  options: SyntaxErrorOptions = {},
): Diagnostic[] {
  const max = options.max ?? MAX_SYNTAX_ERRORS;
  const missing =
    options.missingMessage ?? "Syntax error: incomplete statement";
  const out: Diagnostic[] = [];
  const lines = new Set(options.skipLines ?? []);
  tree.iterate({
    enter: (node) => {
      if (!node.type.isError || out.length >= max) return;
      const line = doc.lineAt(node.from).number;
      if (lines.has(line)) return;
      lines.add(line);
      const text = doc.sliceString(node.from, node.to);
      out.push({
        from: node.from,
        to: node.to,
        severity: "error",
        message: text === "" ? missing : `Syntax error: unexpected "${text}"`,
      });
    },
  });
  return out;
}

/** The syntax errors of an editor state's language tree. */
export function treeSyntaxErrors(
  state: EditorState,
  options: SyntaxErrorOptions = {},
): Diagnostic[] {
  const tree =
    ensureSyntaxTree(state, state.doc.length, 500) ?? syntaxTree(state);
  return syntaxErrorsIn(tree, state.doc, options);
}
